/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { AiOutlineLoading3Quarters } from "react-icons/ai";

import { productType } from '@/config';
import { showToast } from '@/utils/toast';
import { newToast } from '@/utils/newToast';
import OptionItem from './option/OptionItem';
import None from "@/assets/images/default.jpg";
import { useTranslation } from 'react-i18next';
import { getCurrencyConfig } from '@/utils/auth';
import { translateLanguage } from '@/helper/i18n';
import { Dialog } from "@/components/Base/Headless";
import NotificationAlert from '../NotificationAlert';
import { useAppContext } from '@/use-context/Context';
import { apiGetProductByID } from '@/services/ServiceApi';
import FooterDrawer from '../Drawer/FooterDrawer/FooterDrawer';
import { showToastInvalid } from '../Message/showToastInvalid';
import { upadteAndAddToCartItem, updateAddToCart } from '@/utils';
import { convertToBaseAmount, formatPriceByCurrency } from '@/utils/helper';
import { calculatePriceAfterDiscount, calculateTotalPrice, getFileName } from '@/helper';

interface ProductDetailProps {
    isOpen: boolean;
    onClose: () => void;
    getOrderID?: () => void;
    foodItem?: productType
}

const ProductDetail: React.FC<ProductDetailProps> = ({ isOpen, onClose, foodItem }) => {
    const { t } = useTranslation();
    const getCurrency = getCurrencyConfig();
    const { listItems, setListItems, setAddToCart } = useAppContext();

    const [quantity, setQuantity] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [quantityNew, setQuantityNew] = useState<number>(1);
    const [foodItemNew, setFoodItem] = useState<productType>();
    const [detailProduct, setProduct] = useState<productType>();
    const [instructions, setInstructions] = useState<string>("");

    const fetchFoodByID = (id: string, qty: number) => {
        setLoading(true);
        apiGetProductByID(id)
            .then(response => {
                if (response.success) {
                    const result: productType = response.data;
                    result.quantity = qty;
                    setProduct(result);
                    setTimeout(() => setLoading(false), 500);
                }
            })
            .catch(console.error);
    };

    useEffect(() => {
        if (foodItem) fetchFoodByID(foodItem._id, foodItem.quantity);
    }, [foodItem]);

    useEffect(() => {
        getProductItem();
    }, [detailProduct]);

    function getProductItem() {
        if (!detailProduct) return;

        const updatedFoodItems = {
            ...detailProduct,
            discountPercent: foodItem?.discountPercent ?? 0,
            options: (detailProduct.options || []).map(optionGroup => ({
                ...optionGroup,
                values: optionGroup.values.map((optionValue) => ({
                    ...optionValue,
                    isCheck: false
                }))
            })),
            addons: (detailProduct.addons || []).map(addOn => ({
                ...addOn,
                isCheck: false
            }))
        };

        setQuantityNew(detailProduct.quantity || 1);
        setInstructions(
            detailProduct.options.length > 0 || detailProduct.addons?.length > 0
                ? ""
                : detailProduct.instruction
        );
        setQuantity(1);
        setFoodItem(updatedFoodItems);
    }

    const handleSelectOption = (optionId: string, indexValue: number, indexOption: number, maxChoice: number) => {
        setFoodItem(prev => {
            if (!prev) return prev;

            return {
                ...prev,
                options: prev.options.map((opt, i) => {
                    if (i === indexOption && opt._id === optionId) {
                        const currentValue = opt.values[indexValue];
                        const checkedCount = opt.values.filter(v => v.isCheck).length;

                        if (maxChoice === 1) {
                            return {
                                ...opt,
                                values: opt.values.map((v, idx) => ({
                                    ...v,
                                    isCheck: idx === indexValue ? !currentValue.isCheck : false,
                                })),
                            };
                        }

                        // ✅ Normal multi-select behavior

                        if (!currentValue.isCheck && checkedCount >= maxChoice) {
                            showToastInvalid(`You can select only ${maxChoice} items!`);
                            return opt;
                        }

                        return {
                            ...opt,
                            values: opt.values.map((v, idx) =>
                                idx === indexValue ? { ...v, isCheck: !v.isCheck } : v
                            ),
                        };
                    }
                    return opt;
                }),
            };
        });
    };

    const handleDecrease = () => {
        if (!foodItemNew) return;

        if (foodItemNew.options.length || foodItemNew.addons.length) {
            if (quantity === 1) onClose();
            setQuantity(q => Math.max(q - 1, 1));
        } else {
            if (quantityNew === 1) onClose();
            setQuantityNew(q => Math.max(q - 1, 1));
        }
    };

    const handleIncrease = () => {
        if (!foodItemNew) return;

        foodItemNew.options.length || foodItemNew.addons.length
            ? setQuantity(q => q + 1)
            : setQuantityNew(q => q + 1);
    };

    const totalPrice = calculateTotalPrice(quantity, quantityNew, foodItemNew);

    const handleAddToCart = () => {
        const invalidOption = foodItemNew?.options?.find(option => {
            if (option.min > 0) {
                return !option.values.some(value => value.isCheck);
            }
            return false;
        });

        if (invalidOption) {
            showToastInvalid(`Please select at least ${invalidOption.min} item(s) for ${invalidOption.name}`);
            return;
        }

        const maxStock = foodItemNew && foodItemNew.isTrackStock && foodItemNew.inventoryStock ? foodItemNew.inventoryStock.available : 0;

        if (foodItemNew && foodItemNew.isTrackStock) {
            if (foodItemNew.options.length > 0 || foodItemNew.addons.length > 0) {
                if (quantity > maxStock) {
                    newToast("failed-add-stock1", `${maxStock ? `${t("only")} ${maxStock} ${t("availableInStock")}` : t("outOfStock")}`);
                    return;
                }
            } else {
                if (quantityNew > maxStock) {
                    newToast("failed-add-stock1", `${maxStock ? `${t("only")} ${maxStock} ${t("availableInStock")}` : t("outOfStock")}`);
                    return;
                }
            }
        }

        getProductItem();

        const productItem: any = {
            ...foodItemNew,
            discountPercent: foodItemNew?.discountPercent,
            quantity: quantity,
            instruction: instructions,
            priceItem: totalPrice.priceItem,
            totalPriceItem: totalPrice.totalPriceItem,
            status: 0,
            isCheck: true,
            isWaiter: true
        };

        const id = foodItem?._id;
        const disPercent = foodItem?.discountPercent;
        const updatedItems = [...listItems];

        const currentItemIndex = updatedItems.findIndex(item => (item._id === id && item.discountPercent == disPercent));

        if (currentItemIndex !== -1) {
            const currentItem = updatedItems[currentItemIndex];

            if (foodItemNew && (foodItemNew.options.length > 0)) {
                updatedItems[currentItemIndex] = {
                    ...currentItem,
                    quantity: currentItem.quantity ? currentItem.quantity + quantity : quantity,
                    instruction: instructions
                };
                setListItems(updatedItems);
                upadteAndAddToCartItem(productItem, setAddToCart);
            } else {
                updatedItems[currentItemIndex] = {
                    ...currentItem,
                    quantity: quantityNew,
                    instruction: instructions
                };
                setListItems(updatedItems);
                const updatedCurrentItem = {
                    ...currentItem,
                    discountPercent: foodItemNew && foodItemNew.discountPercent ? foodItemNew.discountPercent : 0,
                    instruction: instructions,
                    quantity: quantityNew,
                    priceItem: totalPrice.totalPriceItem,
                    isCheck: true,
                    isWaiter: true
                };
                updateAddToCart(updatedCurrentItem, setAddToCart);
            }
        } else {
            showToast("warning-notification-content");
        }
        onClose();
    }

    const handleClose = () => {
        if (foodItem) {
            getProductItem();
            setQuantityNew(foodItem.quantity ? foodItem.quantity : 1);
            setQuantity(1);
        }
        onClose();
    }

    return (
        <>
            <Dialog open={isOpen} onClose={handleClose} size="lg">
                <Dialog.Panel className="flex flex-col min-h-[20vh] max-h-[90vh]">
                    {
                        loading ? (
                            <div className="h-[20vh] flex items-center justify-center">
                                <AiOutlineLoading3Quarters
                                    size={32}
                                    className="animate-spin inline-block text-[#767676]"
                                />
                            </div>
                        ) : (
                            foodItemNew &&
                            <>
                                <div className="flex-1 overflow-y-auto">
                                    <div className="p-4">
                                        <div className="pt-2">
                                            <p className="text-option text-[#0C0D24]">{t("productDetails")}</p>
                                            <hr className="text-[#EDEDED] my-4" />
                                        </div>
                                        <div>
                                            <div className="grid grid-cols-12 gap-4 gap-y-3">
                                                <div className="col-span-4 border-2 rounded-md h-[180px] w-full">
                                                    <img alt="demo Photo" className="rounded-md h-full w-full object-fil"
                                                        src={foodItemNew.image ? getFileName(foodItemNew.image) : None}
                                                        onError={(e) => (e.currentTarget.src = None)} />
                                                </div>

                                                <div className="col-span-8">
                                                    <div className="flex justify-between items-center">
                                                        <div>
                                                            <div className="text-name-product1 text-[#292B2C] capitalize">{foodItemNew.name}</div>
                                                            <div className="text-lg font-bold mt-2 text-danger">
                                                                {foodItemNew.discountPercent ? (
                                                                    <>
                                                                        <span className="text-gray-500 line-through mr-2 text-[14px]">
                                                                            {
                                                                                formatPriceByCurrency(
                                                                                    convertToBaseAmount(
                                                                                        totalPrice.totalPriceItem,
                                                                                        getCurrency ? getCurrency.code : "USD",
                                                                                        // getCurrency?.rateToUSD
                                                                                    ),
                                                                                    getCurrency?.symbol
                                                                                )
                                                                            }
                                                                        </span>
                                                                        <span>
                                                                            {
                                                                                formatPriceByCurrency(
                                                                                    convertToBaseAmount(
                                                                                        totalPrice.totalPriceItem * (1 - foodItemNew.discountPercent / 100),
                                                                                        getCurrency ? getCurrency.code : "USD",
                                                                                        // getCurrency?.rateToUSD
                                                                                    ),
                                                                                    getCurrency?.symbol
                                                                                )
                                                                            }
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    formatPriceByCurrency(
                                                                        convertToBaseAmount(
                                                                            totalPrice.totalPriceItem,
                                                                            getCurrency ? getCurrency.code : "USD",
                                                                            // getCurrency?.rateToUSD
                                                                        ),
                                                                        getCurrency?.symbol
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="mt-2 font-medium">{t("description")}</div>
                                                    <div className="text-[#6E6D7A] font-normal text-[12px] pt-2 text-capitalize line-clamp-4">
                                                        {
                                                            (foodItemNew.description || foodItemNew.descriptionKH) ?
                                                                translateLanguage(foodItemNew.description, foodItemNew.descriptionKH ? foodItemNew.descriptionKH : foodItemNew.description)
                                                                :
                                                                t("N/A")
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="pt-3">
                                            {
                                                foodItemNew.optionIds && foodItemNew.optionIds.length > 0 &&
                                                <>
                                                    <OptionItem
                                                        items={foodItemNew.options}
                                                        handleSelectOption={handleSelectOption}
                                                    />
                                                </>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="border-t">
                                    <FooterDrawer
                                        price={calculatePriceAfterDiscount(totalPrice.totalPrice, foodItemNew.discountPercent)}
                                        quantity={
                                            foodItemNew &&
                                                (foodItemNew.options.length > 0 || foodItemNew?.addons.length > 0) ? quantity : quantityNew
                                        }
                                        handleDecrease={handleDecrease}
                                        handleIncrease={handleIncrease}
                                        handleAddToCart={handleAddToCart}
                                        onClose={handleClose}
                                    />
                                </div>
                            </>
                        )
                    }
                </Dialog.Panel>
            </Dialog>

            <NotificationAlert />
        </>
    );
};

export default ProductDetail;
